<template lang="html">
  <div
    v-observe-visibility="{
      callback: visibilityChanged,
      intersection: {
        rootMargin: '200px',
      },
    }"
  >
    <div v-if="isVisible">
      <div v-if="!isIgStory">
        <!-- --platform-color feeds the native header treatment (tinted icon
             chip instead of the saturated full-width banner); it is inert on
             web.

             The fallback is Slate Teal, DESIGN.md section 2's secondary colour
             for informational emphasis. It was `#cccccc`, a light grey, and this
             header's text is white in BOTH themes -- so on every deliverable
             whose platform has no brand colour ("Other") the label sat at 1.6:1
             in light and 1.55:1 in dark. Invisible either way.

             Solid, not the `rgba(..., .75)` the platform colours use: at 75%
             over a light surface this composites to about 3:1, which fails
             again. White on solid #2D5264 is 8.39:1, measured. -->
        <div
          class="ui top attached header"
          :style="{
            backgroundColor:
              colors[inferredTypeAsIconClass(deliverable.inferred_type)] ||
              '#2D5264',
            '--platform-color':
              colors[inferredTypeAsIconClass(deliverable.inferred_type)] ||
              '#2D5264',
          }"
        >
          <i
            class="icon"
            :class="inferredTypeAsIconClass(deliverable.inferred_type)"
          />

          <!-- <template v-if="deliverable.url">
          {{ inferredTypeAsLabel(deliverable.inferred_type) | toCamelCase }}
        </template> -->
          <!-- <div v-if="!deliverable.url" class="content"> -->
          <div class="content">
            <dropdown
              class="inline left floated"
              :value="deliverable.inferred_type"
              :include-blank="false"
              :items="deliverableTypes"
              :icon-right="true"
              @change="setInferredType"
            >
              <span slot="menuName" class="text">
                {{
                  inferredTypeAsLabel(deliverable.inferred_type) ||
                  "Choose" | humanize
                }}
              </span>
            </dropdown>
          </div>
        </div>
        <a
          v-if="persisted && deletable"
          class="ui top right floating circular floating icon label white outlined tiny"
          @click.stop.prevent="remove"
        >
          <i class="icon delete fitted" />
        </a>

        <form class="ui form clearing" @submit.stop.prevent="save">
          <div v-if="errors" class="ui error message visible" v-html="errors" />

          <div class="ui fields equal width">
            <div class="field">
              <input
                v-model="deliverable.name"
                name="name"
                class="fluid input"
                placeholder="Enter Title"
                type="text"
              />
            </div>

            <div class="field">
              <div
                class="ui icon input"
                :class="{
                  disabled: isIgStory,
                }"
              >
                <input
                  v-model="deliverable.url"
                  type="text"
                  name="url"
                  class="fluid input"
                  placeholder="Enter URL"
                  @paste="handlePaste"
                />
              </div>
            </div>

            <div class="ui buttons compact">
              <a v-if="!persisted" class="ui button primary" @click="save">
                Add
              </a>
            </div>
          </div>

          <div v-if="!isIgStory" class="toggle-row">
            <a href="#" @click.stop.prevent="toggleMore">
              <span v-if="!showMore">
                <i class="icon right caret fitted" />
                Preview & edit
              </span>
              <span v-if="showMore">
                <i class="icon down caret fitted" />
                Show less
              </span>
            </a>
            <a
              v-if="persisted"
              class="deliverable-id-badge"
              title="Click to copy deliverable ID (useful for support requests)"
              @click.stop.prevent="copyDeliverableId"
            >
              <i class="icon hashtag fitted" />{{ deliverable.id }}
            </a>
          </div>

          <div v-if="showMore" class="ui padded grid stackable">
            <div class="seven wide column">
              <div class="field sticky-preview">
                <label for="">Preview</label>
                <deliverable-card
                  v-if="showMore"
                  ref="previewCard"
                  :key="deliverable.id"
                  :initial-deliverable="deliverable"
                  class="fluid margin-top-zero"
                  @cardLoaded="cardLoaded"
                />
              </div>
            </div>

            <div class="nine wide column">
              <!--
                Each editing area below is an independently collapsible
                section. Sections are NOT mutually exclusive — any number can
                be open at once (that's the whole point; the old stacked
                layout was one 2,000px scroll of always-on fields).

                Section bodies use v-show, never v-if: the Fomantic
                click/outbound dropdowns and the CKEditor instance are
                initialised once against $refs and can't survive being torn
                out of the DOM and re-created on every collapse.
              -->
              <div class="deliverable-sections">
                <div class="deliverable-sections__toolbar">
                  <a
                    href="#"
                    class="deliverable-sections__toggle-all"
                    @click.stop.prevent="toggleAllSections"
                  >
                    {{ allSectionsOpen ? "Collapse all" : "Expand all" }}
                  </a>
                </div>

                <div class="deliverable-section">
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('statistics') }"
                    :aria-expanded="isSectionOpen('statistics') ? 'true' : 'false'"
                    @click="toggleSection('statistics')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      {{ isIgReel ? "Reel" : "Custom" }} Statistics
                    </span>
                    <span
                      v-if="sectionSummary('statistics')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("statistics") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('statistics')"
                    class="deliverable-section__content"
                  >
                    <div class="field">
                      <statistic-field
                        v-for="(
                          statistic, index
                        ) in deliverable.manual_update_statistics"
                        v-if="!isDemographicStatistic(statistic)"
                        :key="statistic.id || index"
                        v-model="deliverable.manual_update_statistics[index]"
                        :metric-types="metricTypes"
                        :existing-labels="existingStatLabels"
                        @remove="removeStatistic"
                        @change="refreshPreviewCard"
                        @estimate-dismissed="onEstimateDismissed"
                      />

                      <a href="#" class="ui" @click.stop.prevent="addStatistic">
                        <i class="icon add fitted" />
                        Add Statistic
                      </a>

                      <demographics-editor
                        v-if="persisted"
                        :deliverable-id="deliverable.id"
                        :initial-statistics="deliverable.manual_update_statistics"
                        @change="refreshPreviewCard"
                      />
                    </div>
                  </div>
                </div>

                <!-- Google Analytics Timeframe Settings -->
                <div v-if="gaConnected" class="deliverable-section">
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('gaTimeframe') }"
                    :aria-expanded="
                      isSectionOpen('gaTimeframe') ? 'true' : 'false'
                    "
                    @click="toggleSection('gaTimeframe')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      Google Analytics Timeframe
                    </span>
                    <span
                      v-if="sectionSummary('gaTimeframe')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("gaTimeframe") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('gaTimeframe')"
                    class="deliverable-section__content"
                  >
                    <div class="field">
                      <div class="two fields">
                        <div class="field">
                          <label>Start Date</label>
                          <div id="ga_start_date">
                            <div class="ui input">
                              <input
                                v-model="gaSettings.ga_start_date"
                                placeholder="Select start date"
                                @change="updateGASettings"
                                type="date"
                              />
                            </div>
                          </div>
                        </div>
                        <div class="field">
                          <label>End Date</label>
                          <div id="ga_end_date">
                            <div class="ui input">
                              <input
                                v-model="gaSettings.ga_end_date"
                                placeholder="Select end date"
                                @change="updateGASettings"
                                type="date"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <small class="">
                        Leave blank to use all available data. Set dates to filter stats to a specific period.
                        <button type="button" class="ui tiny button tertiary" @click="clearGADates" v-if="gaSettings.ga_start_date || gaSettings.ga_end_date">
                          <span class="ui text grey"><i class="icon times fitted" /></span>
                          Clear
                        </button>
                      </small>
                    </div>
                  </div>
                </div>

                <div v-if="persisted" class="deliverable-section">
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('ads') }"
                    :aria-expanded="isSectionOpen('ads') ? 'true' : 'false'"
                    @click="toggleSection('ads')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">Ads</span>
                    <span
                      v-if="sectionSummary('ads')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("ads") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('ads')"
                    class="deliverable-section__content"
                  >
                    <linked-ads
                      :deliverable-id="deliverable.id"
                      :initial-linked-ads="deliverable.linked_ads || []"
                      :meta-ads-connected="!!deliverable.meta_ads_connected"
                      :show-label="false"
                      @changed="refreshPreviewCard"
                    />

                    <template v-if="deliverable.meta_ads_boost_enabled">
                      <div class="ui divider" />
                      <h5 class="ui tiny header margin-bottom-small">Boost this post</h5>
                      <meta-boost
                        :deliverable-id="deliverable.id"
                        :initial-boost="deliverable.current_boost || null"
                        :initial-object-story-id="deliverable.default_object_story_id || ''"
                        :platform="deliverable.default_boost_platform || 'facebook'"
                      />
                    </template>
                  </div>
                </div>

                <div
                  v-if="persisted && isEmailType && trackedUrls.length > 0"
                  class="deliverable-section"
                >
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('clickTracking') }"
                    :aria-expanded="
                      isSectionOpen('clickTracking') ? 'true' : 'false'
                    "
                    @click="toggleSection('clickTracking')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      Track clicks for
                    </span>
                    <span
                      v-if="sectionSummary('clickTracking')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("clickTracking") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('clickTracking')"
                    class="deliverable-section__content"
                  >
                    <div class="field click-tracking-field">
                      <small class="ui grey text">
                        Pick one or more URLs to count only those clicks, or leave
                        empty to use the campaign-wide total.
                      </small>
                      <select
                        ref="clickFilterDropdown"
                        multiple
                        class="ui fluid multiple search selection dropdown click-filter-select margin-top-small"
                        :class="{ disabled: savingClickFilter, loading: savingClickFilter }"
                        placeholder="All links (campaign total)"
                      >
                        <option
                          v-for="link in trackedUrls"
                          :key="link.url"
                          :value="link.url"
                        >
                          {{ truncateUrl(link.url) }} ({{ link.total_clicks | abbreviateNumber }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- GA4 outbound-clicks per-destination picker (url deliverables) -->
                <!--
                  The destination list is cached as a side effect of a GA4 stats
                  refresh, so it is empty for the first few minutes after a link is
                  added. This whole section used to be hidden in that window, which
                  reads as "the feature is gone" rather than "not yet" — a creator
                  added two articles, went looking for the picker, could not find
                  it, and asked support; the destinations cached 85 seconds after
                  their last message. Keep the section and say what is happening.

                  But only where a refresh can actually happen: on earned media the
                  tenant has no GA property for (a worldatlas.com article, say),
                  "once its stats have refreshed" is a promise nothing will keep, so
                  outboundLinksAvailable hides the section outright.
                -->
                <div
                  v-if="persisted && isGoogleAnalytics && outboundLinksAvailable"
                  class="deliverable-section"
                >
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('outboundLinks') }"
                    :aria-expanded="
                      isSectionOpen('outboundLinks') ? 'true' : 'false'
                    "
                    @click="toggleSection('outboundLinks')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      Outbound links to show
                    </span>
                    <span
                      v-if="sectionSummary('outboundLinks')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("outboundLinks") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('outboundLinks')"
                    class="deliverable-section__content"
                  >
                    <div class="field click-tracking-field">
                      <small v-if="trackedOutboundUrls.length === 0" class="ui grey text">
                        The links in this page appear here once its stats have
                        refreshed — usually a few minutes after you add it. Until then
                        the report shows the total outbound clicks.
                      </small>
                      <small v-else class="ui grey text">
                        Pick one or more destinations to show their clicks individually,
                        or leave empty to show the total outbound clicks.
                      </small>
                      <small class="ui grey text outbound-links-hint">
                        These are the links to other websites that Google Analytics
                        recorded clicks on.
                      </small>
                      <select
                        v-if="trackedOutboundUrls.length > 0"
                        ref="outboundFilterDropdown"
                        multiple
                        class="ui fluid multiple search selection dropdown click-filter-select margin-top-small"
                        :class="{ disabled: savingOutboundFilter, loading: savingOutboundFilter }"
                        placeholder="All destinations (total)"
                      >
                        <option
                          v-for="link in trackedOutboundUrls"
                          :key="link.url"
                          :value="link.url"
                        >
                          {{ truncateUrl(link.url) }} ({{ link.total_clicks | abbreviateNumber }})
                        </option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Per-stat show/hide toggle -->
                <div
                  v-if="persisted && statToggles.length > 0"
                  class="deliverable-section"
                >
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('statVisibility') }"
                    :aria-expanded="
                      isSectionOpen('statVisibility') ? 'true' : 'false'
                    "
                    @click="toggleSection('statVisibility')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      Show or hide stats
                    </span>
                    <span
                      v-if="sectionSummary('statVisibility')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("statVisibility") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('statVisibility')"
                    class="deliverable-section__content"
                  >
                    <div class="field stat-visibility-field">
                      <small class="ui grey text">
                        Click a stat to hide it from this deliverable's card and
                        reports. Hidden stats are never deleted.
                      </small>
                      <div class="stat-visibility-chips margin-top-small">
                        <button
                          v-for="stat in statToggles"
                          :key="stat.type"
                          type="button"
                          class="stat-visibility-chip"
                          :class="{ 'stat-visibility-chip--hidden': isStatHidden(stat.type) }"
                          :disabled="savingHiddenStats"
                          @click="toggleStatVisibility(stat.type)"
                        >
                          <i class="icon fitted" :class="isStatHidden(stat.type) ? 'eye slash' : 'check'" />
                          {{ stat.label }}
                        </button>
                      </div>
                      <button
                        v-if="deliverable.hidden_stats_customized"
                        type="button"
                        class="ui tiny basic button margin-top-small"
                        :disabled="savingHiddenStats"
                        @click="resetHiddenStats"
                      >
                        Use account default
                      </button>
                    </div>
                  </div>
                </div>

                <div class="deliverable-section">
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('media') }"
                    :aria-expanded="isSectionOpen('media') ? 'true' : 'false'"
                    @click="toggleSection('media')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">
                      Images &amp; embed
                    </span>
                    <span
                      v-if="sectionSummary('media')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("media") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('media')"
                    class="deliverable-section__content"
                  >
                    <div class="field">
                      <div class="ui checkbox">
                        <input
                          v-model="deliverable.hide_embed"
                          type="checkbox"
                          name="hide_embed"
                        />
                        <label for="hide_embed">Hide embed</label>
                      </div>
                    </div>

                    <div class="field">
                      <label for=""> Add images in a carousel </label>
                      <uploads-index-view
                        v-if="isSectionMounted('media')"
                        :key="'uploader-deliverable-' + deliverable.id"
                        :url="uploadsUrl"
                        :allow-uploads="true"
                        :sort-enabled="true"
                        @updated="updateUploads"
                        @complete="addUpload"
                        @delete="removeUpload"
                      />
                    </div>
                  </div>
                </div>

                <div class="deliverable-section">
                  <button
                    type="button"
                    class="deliverable-section__header"
                    :class="{ 'is-open': isSectionOpen('description') }"
                    :aria-expanded="
                      isSectionOpen('description') ? 'true' : 'false'
                    "
                    @click="toggleSection('description')"
                  >
                    <i class="icon caret right deliverable-section__caret" />
                    <span class="deliverable-section__label">Description</span>
                    <span
                      v-if="sectionSummary('description')"
                      class="deliverable-section__summary"
                    >
                      {{ sectionSummary("description") }}
                    </span>
                  </button>

                  <div
                    v-show="isSectionOpen('description')"
                    class="deliverable-section__content"
                  >
                    <div class="field">
                      <text-editor
                        v-if="isSectionMounted('description')"
                        :key="`cke-${deliverable.id}`"
                        v-model="deliverable.description"
                        :autofocus="false"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <button class="ui transition hidden" type="submit" />
        </form>
      </div>

      <div v-if="isIgStory" class="ui form clearing">
        <statistic-field
          v-for="(statistic, index) in deliverable.manual_update_statistics"
          v-if="!isDemographicStatistic(statistic)"
          :key="statistic.id || index"
          v-model="deliverable.manual_update_statistics[index]"
          :metric-types="metricTypes"
          :existing-labels="existingStatLabels"
          :allow-additions="false"
          @remove="removeStatistic"
          @change="refreshPreviewCard"
        />

        <a href="#" class="ui" @click.stop.prevent="addStatistic">
          <i class="icon add fitted" />
          Add statistic manually
        </a>

        <div class="field margin-top-small">
          <label for=""> Add statistics from screenshots </label>
          <uploads-index-view
            :key="'uploader-deliverable-' + deliverable.id"
            :url="`${uploadsUrl}&has_statistics=true`"
            :allow-uploads="true"
            @complete="addUploadAndSave"
            @delete="removeUploadAndSave"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueObserveVisibility from "vue-observe-visibility";
import Vue from "vue/dist/vue.esm";
import { mapState, mapGetters } from "vuex";
import store from "store";
import textEditor from "components/textEditor.vue";
import EventBus from "components/eventbus";
import Toasted from "vue-toasted";
import _ from "lodash";

import Dropdown from "components/dropdown.vue";
import StatisticField from "views/deliverables/components/statisticField.vue";
import { adoptSavedStatIds } from "lib/adoptSavedStatIds.mjs";
import deliverableCard from "views/deliverables/components/deliverableCard.vue";
import UploadsIndexView from "views/uploads/index.vue";
import LinkedAds from "views/deliverables/components/linkedAds.vue";
import MetaBoost from "views/deliverables/components/metaBoost.vue";
import DemographicsEditor from "views/deliverables/components/demographicsEditor.vue";
import { DEMOGRAPHIC_METRIC_TYPES } from "utils/demographics";

Vue.use(VueObserveVisibility);

Vue.use(Toasted, {
  position: "bottom-right",
  duration: 2000,
  singleton: true,
});

const { $ } = window;

export default {
  store,
  components: {
    Dropdown,
    textEditor,
    StatisticField,
    UploadsIndexView,
    deliverableCard,
    LinkedAds,
    MetaBoost,
    DemographicsEditor,
  },
  props: {
    endpoint: String,
    initialDeliverable: Object,
    metricTypes: Array,
    // [{label, metric_type}] for the rows already fetched onto this
    // deliverable — "Play Count", "Sessions", "Email Opens". Offered at the top
    // of each custom stat's label list so a correction can land on the row it
    // means to replace. See Deliverable#existing_stat_labels.
    existingStatLabels: {
      type: Array,
      default() {
        return [];
      },
    },
    clearAfterSave: Boolean,
    deletable: {
      default: true,
      type: Boolean,
    },
    deliverableTypes: {
      default() {
        return [
          { value: "activecampaign", label: "ActiveCampaign" },
          { value: "aweber", label: "AWeber" },
          { value: "bitly", label: "Bitly" },
          { value: "convertkit", label: "Kit.com (formerly ConvertKit)" },
          { value: "drip", label: "Drip" },
          { value: "facebook", label: "Facebook" },
          { value: "instagram", label: "Instagram Stories" },
          { value: "instagram", label: "Instagram" },
          { value: "mailchimp", label: "MailChimp" },
          { value: "other", label: "Other" },
          { value: "pinterest", label: "Pin" },
          { value: "reddit", label: "Reddit" },
          { value: "sendfox", label: "SendFox" },
          { value: "tik_tok", label: "TikTok" },
          { value: "twitter", label: "Tweet" },
          { value: "url", label: "Article" },
          { value: "youtube", label: "YouTube" },
          { value: "email", label: "Email" },
        ];
      },
      type: Array,
    },
  },
  data() {
    const $this = this;
    return {
      isVisible: false,
      deliverable: $this.initialDeliverable,
      instagramReelsData: null,
      errors: null,
      loading: false,
      currentRequest: null,
      savingClickFilter: false,
      savingOutboundFilter: false,
      savingHiddenStats: false,
      impressionSuggestion: null,
      // Cards with manual stats auto-expand on desktop. On a phone that
      // stacked every card's full preview+edit grid into a 10,000px+ modal,
      // so narrow viewports start collapsed ("Preview & edit" is one tap
      // away; the impressions-suggestion flow still expands explicitly).
      showMore:
        !window.matchMedia("(max-width: 768px)").matches &&
        (($this.initialDeliverable && $this.isIgStory) ||
          ($this.initialDeliverable.manual_update_statistics &&
            $this.initialDeliverable.manual_update_statistics.length > 0 &&
            $this.initialDeliverable.manual_update_statistics[0].value)),
      gaSettings: {
        ga_start_date: ($this.initialDeliverable && $this.initialDeliverable.ga_settings && $this.initialDeliverable.ga_settings.ga_start_date) || '',
        ga_end_date: ($this.initialDeliverable && $this.initialDeliverable.ga_settings && $this.initialDeliverable.ga_settings.ga_end_date) || ''
      },
      // Which edit sections are expanded. Any number can be open at once —
      // opening one never closes another. Seeded in created() from the
      // deliverable's own data (a section with something in it starts open),
      // so a bare deliverable shows one short column instead of every field.
      openSections: {},
      // Sections that have been open at least once. Two heavy widgets
      // (CKEditor, the uploads browser) only mount once their section is
      // first opened — CKEditor 4 inline misbehaves when initialised inside a
      // display:none container, and the uploader fires an XHR per card.
      // Once mounted they stay mounted; collapsing only hides them.
      mountedSections: {},
    };
  },
  computed: {
    ...mapState("deliverable", ["colors"]),
    ...mapGetters("deliverable", [
      "inferredTypeAsIconClass",
      "inferredTypeAsLabel",
    ]),
    isIgReel() {
      return (
        this.initialDeliverable.url &&
        (this.initialDeliverable.url.includes("/reel/") ||
          this.initialDeliverable.url.includes("/reels/"))
      );
    },
    isIgStory() {
      return this.deliverable.inferred_type === "instagram_stories";
    },
    isGoogleAnalytics() {
      return this.deliverable.inferred_type === 'url';
    },
    // Only true when the tenant actually has a GA property connected for this
    // deliverable's domain (computed server-side). Earned-media URLs the tenant
    // has no GA access to (e.g. a press article) return false, so the GA
    // Timeframe controls stay hidden.
    gaConnected() {
      return !!this.deliverable.google_analytics_connected;
    },
    // Computed server-side (Deliverable#outbound_links_available?): is there any
    // path to outbound-click data for this URL? False for earned media the tenant
    // has no GA property for, where the picker could only ever sit on its
    // "refreshing…" placeholder. Stays true once destinations have cached or a
    // selection has been saved, so a picker in use never disappears.
    outboundLinksAvailable() {
      return !!this.deliverable.outbound_links_available;
    },
    // Gate the on-mount suggestion fetch so we don't fan out a request for every
    // URL deliverable that already has impressions (the common, already-reported
    // case) or whose estimate was dismissed.
    shouldFetchImpressionSuggestion() {
      if (this.deliverable.inferred_type !== "url" || !this.deliverable.url) return false;
      if (this.deliverable.impressions_estimate_dismissed) return false;
      const stats = this.deliverable.manual_update_statistics || [];
      const hasImpressions = stats.some(
        (s) => s.metric_type === "impressions" && s.value
      );
      return !hasImpressions;
    },
    isEmailType() {
      return [
        "email",
        "mailerlite",
        "mailchimp",
        "aweber",
        "convertkit",
        "beehiiv",
        "drip",
        "campaignmonitor",
        "constantcontact",
        "sendfox",
        "madmimi",
        "activecampaign",
        "klaviyo",
        "expertsender",
        "flodesk",
      ].includes(this.deliverable.inferred_type);
    },
    trackedUrls() {
      return this.deliverable.tracked_urls || [];
    },
    selectedClickFilter() {
      const raw = this.deliverable.click_filter;
      if (Array.isArray(raw)) return raw;
      if (raw) return [raw];
      return [];
    },
    trackedOutboundUrls() {
      return this.deliverable.tracked_outbound_urls || [];
    },
    selectedOutboundFilter() {
      const raw = this.deliverable.outbound_filter;
      if (Array.isArray(raw)) return raw;
      if (raw) return [raw];
      return [];
    },
    // One toggle per stat currently on the deliverable, labelled by its headline
    // ("Outbound Clicks", "Pageviews", …). Drives the show/hide checkboxes.
    //
    // total.label defaults server-side to the bare metric_type string (see
    // Deliverable#statisticsValues: `total = {label: type, ...}`) and only
    // gets a nicer label for types with special-case handling — so a plain
    // `|| humanize` fallback never kicks in for the common case where
    // total.label IS present, just equal to the raw type ("engagements",
    // "search_position", …). Humanize whenever it hasn't been overridden.
    //
    // statisticsValues has one entry per type in Statistic.metric_types (the
    // full whitelist — revenue, conversions, search_position, …) regardless
    // of whether this deliverable has ever recorded that type, so toggling
    // "on" the raw list clutters it with types that have no data at all.
    // Match deliverableCard.vue's nonZeroSecondaryStats filter so this list
    // only offers to hide stats that would actually show somewhere.
    statToggles() {
      const values = this.deliverable.statisticsValues || [];
      return values
        .filter(
          (s) =>
            s &&
            s.type &&
            s.total &&
            s.total.value != 0 &&
            s.total.value != "" &&
            s.total.value != "00:00:00"
        )
        .map((s) => ({
          type: s.type,
          label:
            s.total && s.total.label && s.total.label !== s.type
              ? s.total.label
              : this.$options.filters.humanize(s.type),
        }));
    },
    // Keys of the sections actually rendered for this deliverable, in the
    // order they appear. Mirrors the v-if on each section — Expand/Collapse
    // all must not toggle a section the user can't see.
    visibleSections() {
      const keys = ["statistics"];
      if (this.gaConnected) keys.push("gaTimeframe");
      if (this.persisted) keys.push("ads");
      if (this.persisted && this.isEmailType && this.trackedUrls.length > 0) {
        keys.push("clickTracking");
      }
      // Rendered for every GA-connected deliverable, even before its destinations
      // have cached — the section explains the wait rather than vanishing.
      if (this.persisted && this.isGoogleAnalytics && this.outboundLinksAvailable) {
        keys.push("outboundLinks");
      }
      if (this.persisted && this.statToggles.length > 0) {
        keys.push("statVisibility");
      }
      keys.push("media", "description");
      return keys;
    },
    allSectionsOpen() {
      return this.visibleSections.every((key) => this.isSectionOpen(key));
    },
    hasDescription() {
      const description = this.deliverable.description || "";
      return description.replace(/<[^>]*>/g, "").trim().length > 0;
    },
    persisted() {
      return this.deliverable.id;
    },
    uploadsUrl() {
      return `/uploads?assetable_type=Deliverable&assetable_id=${this.deliverable.id}`;
    },
    url() {
      let url = `${this.endpoint}`;
      if (this.persisted) {
        url += `/${this.deliverable.id}`;
      }
      return url;
    },
    requestType() {
      let type = `post`;
      if (this.persisted) {
        type = `patch`;
      }
      return type;
    },
    asParams() {
      const params = {
        deliverable: this.deliverable,
      };

      this.$set(
        params.deliverable,
        "statistics_attributes",
        this.deliverable.manual_update_statistics
      );
      return params;
    },
  },
  watch: {
    "deliverable.hide_embed": function (newVal, old) {
      if (newVal !== old) {
        this.update();
      }
    },
    "deliverable.name": function (newVal, old) {
      if (newVal !== old) {
        this.update();
      }
    },
    "deliverable.url": function (newVal, old) {
      if (newVal !== old) {
        this.update();
        // Only earned-media article URLs get an impressions estimate. Note this
        // watcher NULLS inferred_type two lines down, so by the time the response
        // lands there is no type left to check — which is how a YouTube Short ended
        // up carrying youtube.com's sitewide traffic (6.2 billion impressions on a
        // post with 215 views). The server refuses platform URLs too; this just
        // stops us asking. `update()` re-classifies and the mount-time gate
        // (shouldFetchImpressionSuggestion) covers a URL that resolves to an article.
        if (!this.isSocialPostUrl(newVal)) {
          this.fetchImpressionSuggestion(newVal);
        }
      }
      if (newVal) {
        this.deliverable.inferred_type = null;
      }
    },
    "deliverable.description": function (newVal, old) {
      if ((old && newVal !== old) || (!old && newVal != "")) {
        this.update();
      }
    },
    "deliverable.inferred_type": function (newVal, old) {
      if (newVal && newVal !== old) {
        this.update();
      }
    },
    gaConnected: function (newVal, oldVal) {
      // When GA becomes connected for this deliverable, initialize the date
      // pickers once the (now-visible) Timeframe section renders.
      if (newVal && !oldVal) {
        this.$nextTick(() => {
          // Wait for DOM to update, then initialize calendars
          setTimeout(() => {
            this.initGACalendars();
          }, 100);
        });
      }
    },
    // Rebuild Fomantic's cached menu when the tracked URL set changes
    // (e.g. a stats refresh surfaced new links).
    "trackedUrls.length"() {
      this.teardownClickFilterDropdown();
      this.$nextTick(() => this.initClickFilterDropdown());
    },
    // Same as above for the outbound-destination picker.
    "trackedOutboundUrls.length"() {
      this.teardownOutboundFilterDropdown();
      this.$nextTick(() => this.initOutboundFilterDropdown());
    },
  },
  created() {
    // Computed properties are available here (they are not inside data()),
    // which is what the content-aware defaults need.
    const defaults = this.defaultOpenSections();
    this.openSections = defaults;
    this.mountedSections = Object.assign({}, defaults);
  },
  mounted() {
    EventBus.$on("saveAllDeliverables", this.save);
    this.loadEmbed();

    if (
      this.deliverable.manual_update_statistics.length < 1 &&
      !this.isIgStory
    ) {
      this.initManualStats();
    }

    // Bulk "Add URLs" sets the URL at creation, so the deliverable.url watcher
    // (which fires on *change*) never runs for it. Fetch the impressions
    // suggestion once on mount — but ONLY for URL deliverables that could
    // actually use one: no existing impressions value and not previously
    // dismissed. This keeps existing/complete deliverables (the bulk of what's
    // out there, already in reports) from firing a request on every render.
    if (this.shouldFetchImpressionSuggestion) {
      this.fetchImpressionSuggestion(this.deliverable.url);
    }

    // Init the GA date pickers only when GA is actually connected (the
    // Timeframe section is hidden otherwise).
    if (this.gaConnected) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.initGACalendars();
        }, 100);
      });
    }

    if (typeof deliverablesChannel !== "undefined") {
      const $this = this;
      window.deliverablesChannel(
        $this.deliverable.id,
        "deliverable-manual-stats-updated",
        function (data) {
          $this.$set(
            $this.deliverable,
            "manual_update_statistics",
            data.statistics
          );
        }
      );
    }
    // if (typeof hb !== "undefined") {
    //   const channel = hb.pusher.subscribe(`deliverable-${this.deliverable.id}`);

    //   channel.bind("deliverable-manual-stats-updated", (data) => {
    //     this.$set(this.deliverable, "manual_update_statistics", data);
    //   });
    // }
  },
  updated() {
    this.loadEmbed();
    // Init the click-filter dropdown the first time its <select> appears
    // in the DOM (showMore toggles it in). Don't re-init on later updates —
    // `set exactly` fires onChange twice (clear + set), which would ping-pong
    // against the server and clobber the user's selection.
    this.$nextTick(() => {
      if (this.$refs.clickFilterDropdown && !this._clickFilterDropdownLoaded) {
        this.initClickFilterDropdown();
      }
      if (
        this.$refs.outboundFilterDropdown &&
        !this._outboundFilterDropdownLoaded
      ) {
        this.initOutboundFilterDropdown();
      }
    });
  },
  destroyed() {
    EventBus.$off("saveAllDeliverables", this.save);
    this.teardownClickFilterDropdown();
  },
  methods: {
    visibilityChanged(value) {
      this.isVisible = this.isVisible || value;
    },
    // A section starts open when it already has something in it. Statistics
    // is the reason people open this panel, so it is always open to start.
    defaultOpenSections() {
      const deliverable = this.deliverable || {};
      return {
        statistics: true,
        gaTimeframe: !!(
          this.gaSettings.ga_start_date || this.gaSettings.ga_end_date
        ),
        ads:
          (deliverable.linked_ads || []).length > 0 ||
          !!deliverable.current_boost,
        clickTracking: this.selectedClickFilter.length > 0,
        outboundLinks: this.selectedOutboundFilter.length > 0,
        statVisibility: (deliverable.hidden_stats || []).length > 0,
        media:
          !!deliverable.hide_embed || (deliverable.uploads || []).length > 0,
        description: this.hasDescription,
      };
    },
    isSectionOpen(key) {
      return !!this.openSections[key];
    },
    // True once the section has been opened at least once — gates the
    // lazily-mounted widgets (see `mountedSections`).
    isSectionMounted(key) {
      return !!this.mountedSections[key];
    },
    toggleSection(key) {
      this.setSectionOpen(key, !this.isSectionOpen(key));
    },
    setSectionOpen(key, open) {
      this.$set(this.openSections, key, open);
      if (open) {
        this.$set(this.mountedSections, key, true);
      }
    },
    toggleAllSections() {
      const open = !this.allSectionsOpen;
      this.visibleSections.forEach((key) => this.setSectionOpen(key, open));
    },
    // Short "there's something in here" hint on a collapsed header, so the
    // user doesn't have to open every section to find where the data is.
    sectionSummary(key) {
      const deliverable = this.deliverable || {};

      if (key === "statistics") {
        const count = (deliverable.manual_update_statistics || []).filter(
          (statistic) =>
            statistic &&
            statistic.metric_type &&
            !this.isDemographicStatistic(statistic) &&
            statistic.value !== null &&
            statistic.value !== undefined &&
            statistic.value !== ""
        ).length;
        return count > 0 ? `${count} ${count === 1 ? "stat" : "stats"}` : null;
      }

      if (key === "gaTimeframe") {
        return this.gaSettings.ga_start_date || this.gaSettings.ga_end_date
          ? "Custom range"
          : null;
      }

      if (key === "ads") {
        const parts = [];
        const count = (deliverable.linked_ads || []).length;
        if (count > 0) parts.push(`${count} linked`);
        if (deliverable.current_boost) parts.push(deliverable.current_boost.status);
        return parts.length > 0 ? parts.join(" · ") : null;
      }

      if (key === "clickTracking") {
        const count = this.selectedClickFilter.length;
        return count > 0 ? `${count} selected` : null;
      }

      if (key === "outboundLinks") {
        const count = this.selectedOutboundFilter.length;
        return count > 0 ? `${count} selected` : null;
      }

      if (key === "statVisibility") {
        // Only the chips listed below: a channel default can hide a stat
        // this post type doesn't have (Paid Reach on a Story).
        const count = this.statToggles.filter((stat) => this.isStatHidden(stat.type)).length;
        return count > 0 ? `${count} hidden` : null;
      }

      if (key === "media") {
        const parts = [];
        const count = (deliverable.uploads || []).length;
        if (count > 0) {
          parts.push(`${count} ${count === 1 ? "image" : "images"}`);
        }
        if (deliverable.hide_embed) parts.push("embed hidden");
        return parts.length > 0 ? parts.join(" · ") : null;
      }

      if (key === "description") {
        return this.hasDescription ? "Added" : null;
      }

      return null;
    },
    handlePaste(e) {
      let text = e.clipboardData.getData("Text");
      if (text.includes("background-image")) {
        this.instagramReelsData = text;

        var reelUrl = `https://www.instagram.com${
          text.match(/.*href="(.*?)".*/)[1]
        }`;
        this.deliverable.url = reelUrl;

        $(e.target).popup({
          on: "manual",
          content: "👍 Reels stats added!",
          position: "top center",
          variation: "inverted",
        });
        $(e.target).popup("show");

        this.update();
        e.preventDefault();
        return false;
      }
    },
    // Cheap client-side twin of PublicationTrafficEstimator.publication_url? — it
    // only saves a round trip, so it does not need to enumerate every platform the
    // server knows. The server is the gate; this is politeness.
    isSocialPostUrl(url) {
      return /(^|\.)(youtube\.com|youtu\.be|instagram\.com|tiktok\.com|facebook\.com|fb\.watch|linkedin\.com|pinterest\.[a-z.]+|twitter\.com|x\.com|threads\.(net|com)|bsky\.app|twitch\.tv|reddit\.com)\//i.test(
        String(url || "").replace(/^https?:\/\//i, "")
      );
    },

    fetchImpressionSuggestion(url) {
      if (!url || url.length < 4) {
        this.impressionSuggestion = null;
        return;
      }
      const $this = this;
      window.$.ajax({
        url: "/deliverables/suggest_impressions",
        type: "get",
        dataType: "json",
        data: {url: url},
        success(data) {
          if (data && data.impressions) {
            $this.impressionSuggestion = {
              value: data.impressions,
              domain: data.domain,
              estimated: !!data.estimated,
            };
            $this.applySuggestion();
          } else {
            $this.impressionSuggestion = null;
          }
        },
        error() {
          $this.impressionSuggestion = null;
        },
      });
    },

    applySuggestion() {
      if (!this.impressionSuggestion) return;
      // The user previously cleared a guessed value for this deliverable — don't
      // bring it back.
      if (this.deliverable.impressions_estimate_dismissed) return;

      const stats = this.deliverable.manual_update_statistics;
      if (!stats || stats.length === 0) return;

      // Never suggest when the deliverable can produce its own real impressions:
      // a connected GA property for this domain (e.g. the tenant's own blog),
      // where a guessed number is both misleading and unnecessary.
      if (this.gaConnected) return;

      // Don't touch a deliverable that already has an impressions value —
      // manually entered, previously ESTIMATED, or fetched (GA). The old guard
      // only checked `manual_update_statistics`, so a deliverable with real
      // GA-fetched impressions (manual_update:false) still got a guess stamped
      // on top. And because the payload used to omit saved estimates, this list
      // looked empty on every open and a fresh estimate was suggested (and
      // saved) again — that's how a deliverable ends up with two identical
      // estimated rows. The payload now carries them, so this guard sees them.
      const hasExistingImpressions = stats.some(
        (s) => s.metric_type === "impressions"
      );
      const hasFetchedImpressions = (this.deliverable.statisticsValues || []).some(
        (s) => s && s.type === "impressions" && s.total && Number(s.total.value) > 0
      );
      if (hasExistingImpressions || hasFetchedImpressions) return;

      // Pre-fill the first empty slot as a *labeled estimate*, NEVER as an
      // authoritative manual value. Persisting a suggestion as `manual_update:true`
      // was the #1038 regression: it masked real GA values and — because
      // suggest_impressions replays the most-recent *manual* impressions for a
      // domain — propagated the same number onto every same-domain deliverable (one
      // flat figure on every card).
      //
      // `estimated:true, manual_update:false` structurally prevents both harms and
      // is the whole guard: statisticsOfType ranks estimated BELOW real and manual
      // rows for the same label (so it can't mask a GA value), and the replay query
      // reads manual_update:true only (so it can't self-propagate). The row is
      // therefore safe to save as-is, and must be — this is a value the user can
      // see on screen. Do NOT add `suggested: true` back here: the server drops
      // those rows silently, which is exactly how the number ended up displayed but
      // never persisted, invisible in every report.
      //
      // A real user edit still takes ownership and flips it to manual via
      // statisticField#onUserEditValue.
      const firstStat = stats[0];
      if (!firstStat.value && !firstStat.metric_type) {
        this.$set(stats, 0, {
          deliverable_id: firstStat.deliverable_id,
          metric_type: "impressions",
          label: "Impressions",
          value: String(this.impressionSuggestion.value),
          manual_update: false,
          estimated: true,
        });
        this.showMore = true;
      }
    },

    onEstimateDismissed() {
      this.$set(this.deliverable, "impressions_estimate_dismissed", true);
      this.impressionSuggestion = null;
      // Persist the dismissal, immediately and on its own. It used to live only
      // in the open tab, which was harmless while an estimate was never saved in
      // the first place; now that a saved estimate renders here, an unpersisted
      // flag means the next page load suggests (and saves) the same number again.
      // Not routed through the debounced whole-form save: that fires 750ms later
      // and is lost if the user navigates away, which is exactly what people do
      // right after deleting a row. Deleting a SAVED estimate is also recorded
      // server-side by StatisticsController#destroy; this one-field PATCH covers
      // clearing the value, which never reaches that endpoint.
      if (!this.deliverable.id) return;
      window.$.ajax({
        url: `/deliverables/${this.deliverable.id}`,
        type: "patch",
        dataType: "json",
        data: { deliverable: { impressions_estimate_dismissed: true } },
      });
    },

    initManualStats() {
      this.$set(this.deliverable, "manual_update_statistics", [
        { deliverable_id: this.deliverable.id },
      ]);
      this.applySuggestion();
    },
    mergeManualStats(stats) {
      this.adoptSavedStatIds(stats);
      stats.forEach((stat) => {
        const existingStat = _.find(this.deliverable.manual_update_statistics, [
          "id",
          stat.id,
        ]);
        if (existingStat) {
          Object.assign(existingStat, stat);
        }
      });
    },
    // See lib/adoptSavedStatIds.mjs: without this every autosave re-inserts
    // each row it never learned the id of.
    adoptSavedStatIds(savedStats) {
      adoptSavedStatIds(
        this.deliverable.manual_update_statistics,
        savedStats,
        (row, id) => this.$set(row, "id", id)
      );
    },
    cardLoaded(deliverable) {
      if (typeof window.iframely !== "undefined") {
        window.iframely.load();
      }
      this.$set(this.deliverable, "inferred_type", deliverable.inferred_type);
      // The preview card reloads its own (separate) copy of the deliverable
      // whenever a stat is added/edited (refreshPreviewCard), but that never
      // touched this component's own `deliverable.statisticsValues` — so
      // statToggles (the Show/hide stats list) kept showing whatever was
      // there on initial page load, missing anything added since. Sync it
      // here, on the same refresh this component already listens for.
      this.$set(this.deliverable, "statisticsValues", deliverable.statisticsValues);
      if (deliverable.manual_update_statistics.length > 0) {
        this.mergeManualStats(deliverable.manual_update_statistics);
      } else {
        this.initManualStats();
      }
    },
    setInferredType(type) {
      this.$set(this.deliverable, "inferred_type", type);
    },
    updateUploads(uploads) {
      this.$set(
        this.deliverable,
        "uploads",
        uploads.map((u) => u.asset)
      );
    },
    addUploadAndSave(asset) {
      this.addUpload(asset);
      this.save();
    },
    addUpload(asset) {
      this.deliverable.uploads.push(asset);
    },
    removeUploadAndSave(upload) {
      this.removeUpload(upload);
      // this.save();
    },
    removeUpload(upload) {
      const i = _.findIndex(this.deliverable.uploads, { id: upload.asset.id });
      this.deliverable.uploads.splice(i, 1);
    },

    loadEmbed() {
      this.$nextTick(() => {
        $(this.$el).find(".ui.embed").embed();
      });
    },
    refreshPreviewCard() {
      const $this = this;
      if ($this.$refs.previewCard) {
        $this.$refs.previewCard.loadData();
      }
    },
    isDemographicStatistic(statistic) {
      return DEMOGRAPHIC_METRIC_TYPES.includes(statistic.metric_type);
    },
    copyDeliverableId() {
      const id = this.deliverable.id;
      navigator.clipboard.writeText(String(id)).then(() => {
        this.$toasted.show(`Copied deliverable ID #${id}`);
      });
    },
    removeStatistic(statistic) {
      this.refreshPreviewCard();

      if (statistic.id) {
        // eslint-disable-next-line no-alert
        this.deliverable.manual_update_statistics = this.deliverable.manual_update_statistics.filter(
          (x) => x.id !== statistic.id
        );
      } else {
        this.deliverable.manual_update_statistics = this.deliverable.manual_update_statistics.filter(
          (x) => x !== statistic
        );
      }
    },
    addStatistic() {
      this.$set(
        this.deliverable,
        "manual_update_statistics",
        this.deliverable.manual_update_statistics || []
      );
      this.deliverable.manual_update_statistics.push({
        deliverable_id: this.deliverable.id,
      });
    },
    toggleMore() {
      this.showMore = !this.showMore;
    },
    remove() {
      this.deleteDeliverable();
    },
    deleteDeliverable() {
      const $this = this;
      // eslint-disable-next-line no-alert
      if (window.confirm("Are you sure you want to delete this deliverable?")) {
        $this.$emit("delete", $this.deliverable);
        if ($this.persisted) {
          $.destroy(`/deliverables/${$this.deliverable.id}`);
        }
      }
    },
    save() {
      if (!this.deliverable.name && !this.deliverable.url) {
        return false;
      }

      this.errors = null;
      this.loading = true;
      const $this = this;

      let params = { ...$this.asParams };
      if (this.instagramReelsData) {
        params.instagram_reels_data = this.instagramReelsData;
        this.instagramReelsData = null;
      }
      // Include GA settings if this is a Google Analytics deliverable
      if (this.isGoogleAnalytics && (this.gaSettings.ga_start_date || this.gaSettings.ga_end_date)) {
        params.deliverable.ga_settings = this.gaSettings;
      }

      this.currentRequest = window.$.ajax({
        url: $this.url,
        type: $this.requestType,
        dataType: "json",
        data: params,
        beforeSend: function () {
          if ($this.currentRequest != null) {
            $this.currentRequest.abort();
          }
        },
        success: $this.saveSuccess,
        error: $this.saveError,
        complete: $this.saveComplete,
      });
    },
    saveComplete() {
      if (this.clearAfterSave) {
        this.deliverable = {};
      }
      // this.hideSearchModal();
      this.loading = false;
    },
    saveSuccess(data) {
      this.deliverable.id = data.id;

      if (data.manual_update_statistics.length > 0) {
        this.adoptSavedStatIds(data.manual_update_statistics);
      } else {
        this.initManualStats();
      }
      // Server-computed from the URL's domain, and absent on a deliverable that
      // has never been saved — so until this lands, applySuggestion's GA guard
      // reads false on exactly the new deliverables it exists to protect.
      this.$set(
        this.deliverable,
        "google_analytics_connected",
        data.google_analytics_connected
      );
      this.$set(this.deliverable, "statisticsValues", data.statisticsValues);
      this.$set(this.deliverable, "inferred_type", data.inferred_type);
      // Server-computed and URL-dependent, so it can flip on any save (a new
      // deliverable has no value at all until the first response). Without this
      // the outbound-links section would stay hidden until a full page reload,
      // even on a GA-connected domain where it should be showing its "links
      // appear once stats refresh" note.
      this.$set(
        this.deliverable,
        "outbound_links_available",
        data.outbound_links_available
      );
      this.$emit("save", data);
      Vue.toasted.success("Saved");
    },
    saveError(request) {
      this.loading = false;
      if (request.statusText == "abort") {
        //nothing
      } else if (request.responseJSON) {
        this.errors = `Error: ${request.responseJSON.errors}`;
      } else {
        this.errors = "There was an error saving your data.";
      }
    },
    // eslint-disable-next-line prefer-arrow-callback
    update: _.debounce(function () {
      if (this.persisted) {
        this.save();
      }
    }, 750),
    updateGASettings() {
      // Update GA settings when date fields change
      if (this.persisted && (this.gaSettings.ga_start_date && this.gaSettings.ga_end_date)) {
        this.save();
      }
      if (this.persisted && (!this.gaSettings.ga_start_date && !this.gaSettings.ga_end_date)) {
        this.save();
      }
    },
    initGACalendars() {
      return;
      // const $this = this;

      // // Only initialize if this is a Google Analytics deliverable
      // if (!this.isGoogleAnalytics) return;

      // const thisEl = $(this.$el);
      
      // thisEl.find("#ga_end_date").calendar({
      //   type: 'date',
      //   startCalendar: $("#ga_start_date"),
      //   onChange(date, text) {
      //     $this.gaSettings.ga_end_date = text;
      //     $this.updateGASettings();
      //   }
      // });
      // thisEl.find("#ga_start_date").calendar({
      //   type: 'date',
      //   endCalendar: $("#ga_end_date"),
      //   onChange(date, text) {
      //     $this.gaSettings.ga_start_date = text;
      //     $this.updateGASettings();
      //   }
      // });
    },
    clearGADates() {
      // Clear both dates
      this.gaSettings.ga_start_date = '';
      this.gaSettings.ga_end_date = '';

      // Clear the calendar inputs
      // $("#ga_start_date").calendar('clear');
      // $("#ga_end_date").calendar('clear');

      // Trigger update
      this.updateGASettings();
    },
    truncateUrl(url) {
      if (!url) return "";
      const stripped = url.replace(/^https?:\/\//, "").replace(/^www\./, "");
      return stripped.length > 55 ? stripped.slice(0, 55) + "…" : stripped;
    },
    teardownClickFilterDropdown() {
      const el = this.$refs.clickFilterDropdown;
      if (!el || !window.jQuery) {
        this._clickFilterDropdownLoaded = false;
        return;
      }
      const $el = window.jQuery(el);
      try {
        $el.dropdown("destroy");
      } catch (e) {
        // noop — Fomantic throws if the dropdown was never initialised
      }
      this._clickFilterDropdownLoaded = false;
    },
    initClickFilterDropdown() {
      const el = this.$refs.clickFilterDropdown;
      if (!el || !window.jQuery || this._clickFilterDropdownLoaded) return;
      const $el = window.jQuery(el);

      // Build the dropdown. `set exactly` below fires onChange twice (once on
      // its internal clear, once on the actual set) — suppress those calls so
      // we only react to real user interaction.
      this._suppressClickFilterOnChange = true;
      $el.dropdown({
        forceSelection: false,
        fullTextSearch: true,
        onChange: (value) => {
          if (this._suppressClickFilterOnChange) return;
          // Fomantic passes an Array for multi-select dropdowns, and a
          // comma-joined string for single-select. Handle both.
          let selected;
          if (Array.isArray(value)) {
            selected = value.filter((v) => v);
          } else if (typeof value === "string" && value.length > 0) {
            selected = value.split(",");
          } else {
            selected = [];
          }
          const current = this.selectedClickFilter;
          if (
            selected.length === current.length &&
            selected.every((v, i) => v === current[i])
          ) {
            return;
          }
          this.saveClickFilter(selected);
        },
      });
      $el.dropdown("set exactly", this.selectedClickFilter);
      this._suppressClickFilterOnChange = false;
      this._clickFilterDropdownLoaded = true;
    },
    saveClickFilter(urls) {
      this.savingClickFilter = true;
      // jQuery drops an empty array from the payload entirely, which would
      // leave click_filter absent and the controller wouldn't clear it. Send
      // [""] so Rails sees params[:deliverable][:click_filter] = [""]; the
      // controller's reject(&:blank?) turns that into [].
      const payload = urls.length > 0 ? urls : [""];
      window.$.ajax({
        url: `/deliverables/${this.deliverable.id}.json`,
        type: "patch",
        dataType: "json",
        data: { deliverable: { click_filter: payload } },
      })
        .done((data) => {
          // Only update the Vue state — do NOT call `set exactly` on the
          // dropdown here. The DOM already reflects the user's click, and
          // pushing the value back would re-trigger Fomantic's onChange loop.
          this.$set(this.deliverable, "click_filter", data.click_filter);
          this.$set(this.deliverable, "tracked_urls", data.tracked_urls);
          this.$set(this.deliverable, "statisticsValues", data.statisticsValues);
          this.refreshPreviewCard();
        })
        .always(() => {
          this.savingClickFilter = false;
        });
    },
    teardownOutboundFilterDropdown() {
      const el = this.$refs.outboundFilterDropdown;
      if (!el || !window.jQuery) {
        this._outboundFilterDropdownLoaded = false;
        return;
      }
      const $el = window.jQuery(el);
      try {
        $el.dropdown("destroy");
      } catch (e) {
        // noop — Fomantic throws if the dropdown was never initialised
      }
      this._outboundFilterDropdownLoaded = false;
    },
    initOutboundFilterDropdown() {
      const el = this.$refs.outboundFilterDropdown;
      if (!el || !window.jQuery || this._outboundFilterDropdownLoaded) return;
      const $el = window.jQuery(el);

      // Mirrors initClickFilterDropdown: `set exactly` fires onChange twice
      // (internal clear + the real set), so suppress those programmatic calls.
      this._suppressOutboundFilterOnChange = true;
      $el.dropdown({
        forceSelection: false,
        fullTextSearch: true,
        onChange: (value) => {
          if (this._suppressOutboundFilterOnChange) return;
          let selected;
          if (Array.isArray(value)) {
            selected = value.filter((v) => v);
          } else if (typeof value === "string" && value.length > 0) {
            selected = value.split(",");
          } else {
            selected = [];
          }
          const current = this.selectedOutboundFilter;
          if (
            selected.length === current.length &&
            selected.every((v, i) => v === current[i])
          ) {
            return;
          }
          this.saveOutboundFilter(selected);
        },
      });
      $el.dropdown("set exactly", this.selectedOutboundFilter);
      this._suppressOutboundFilterOnChange = false;
      this._outboundFilterDropdownLoaded = true;
    },
    saveOutboundFilter(urls) {
      this.savingOutboundFilter = true;
      // jQuery drops an empty array from the payload, leaving outbound_filter
      // absent (the controller wouldn't clear it). Send [""] so Rails sees the
      // key; its reject(&:blank?) turns that into [].
      const payload = urls.length > 0 ? urls : [""];
      window.$.ajax({
        url: `/deliverables/${this.deliverable.id}.json`,
        type: "patch",
        dataType: "json",
        data: { deliverable: { outbound_filter: payload } },
      })
        .done((data) => {
          this.$set(this.deliverable, "outbound_filter", data.outbound_filter);
          this.$set(
            this.deliverable,
            "tracked_outbound_urls",
            data.tracked_outbound_urls
          );
          this.$set(this.deliverable, "statisticsValues", data.statisticsValues);
          this.refreshPreviewCard();
        })
        .always(() => {
          this.savingOutboundFilter = false;
        });
    },
    isStatHidden(type) {
      return (this.deliverable.hidden_stats || []).includes(type);
    },
    toggleStatVisibility(type) {
      const current = this.deliverable.hidden_stats || [];
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : current.concat(type);
      this.saveHiddenStats(next);
    },
    saveHiddenStats(hidden) {
      // [""] sentinel for the empty case, same reason as saveOutboundFilter.
      // An explicit empty list is "show everything on this post", which
      // overrides the account's default for the channel.
      const payload = hidden.length > 0 ? hidden : [""];
      this.patchHiddenStats({ hidden_stats: payload });
    },
    // Drop this post's own list so it follows the account default again.
    resetHiddenStats() {
      this.patchHiddenStats({ reset_hidden_stats: "1" });
    },
    patchHiddenStats(deliverableParams) {
      this.savingHiddenStats = true;
      window.$.ajax({
        url: `/deliverables/${this.deliverable.id}.json`,
        type: "patch",
        dataType: "json",
        data: { deliverable: deliverableParams },
      })
        .done((data) => {
          this.$set(this.deliverable, "hidden_stats", data.hidden_stats);
          this.$set(
            this.deliverable,
            "hidden_stats_customized",
            data.hidden_stats_customized
          );
          this.refreshPreviewCard();
        })
        .always(() => {
          this.savingHiddenStats = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
/* #deliverablesSearchModal {
  position: fixed;
} */

.ui.top.attached.header {
  text-transform: capitalize;
  color: #fff;
  margin: -1em;
  margin-bottom: 1em;
  border-width: 0;
}

div.ui[class*="9:16"].embed {
  padding-bottom: 177%;
}

.top.right.attached.label {
  z-index: 3;
}

.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.deliverable-id-badge {
  // Tokens, not a literal black: at 30% on the dark surface this measured
  // 1.08:1, i.e. gone. It is the deliverable's id, which is what someone
  // reaches for when they need to quote a specific row to support.
  color: var(--text-muted);
  // 13px, not `0.8rem`. The app's root is 14px, not 16, so that computed to
  // 11px -- under the legibility floor, on text that is a reference number and
  // therefore has to be read character by character.
  font-size: 13px;
  cursor: pointer;

  .icon {
    margin-right: 0.15rem;
  }

  &:hover {
    color: var(--text-secondary);
  }
}

// The edit column (custom stats, GA settings, linked ads, tags, description,
// etc.) runs much longer than the preview card, so without this the card
// scrolls out of view almost immediately, defeating the point of a live
// preview while editing further-down fields.
//
// Deliberately NOT setting max-height/overflow-y here: the card's own
// Fomantic popups (stat details, demographics) are positioned to pop out
// past the card's right edge, and setting overflow-y without overflow-x
// makes the browser compute overflow-x: auto too (CSS doesn't allow
// "visible" on one axis and not the other) — clipping those popups instead
// of letting them float over the surrounding content.
//
// z-index is required too: `position: sticky` creates its own stacking
// context, so the popup's own (high) z-index is scoped inside it and can't
// escape to paint above later siblings (the custom-stats/edit column) —
// without this the popup renders in full but underneath that column's text.
.sticky-preview {
  position: sticky;
  top: 1rem;
  align-self: flex-start;
  z-index: 2;
}

// Collapsible edit sections.
//
// Deliberately NOT Fomantic's `.ui.accordion`: its CSS hides *any* descendant
// `.content` that isn't `.active`, which would blank out the Fomantic list
// items, cards and segments the linked-ads / uploads children render inside
// each section. Same look, own class names, open state driven by Vue instead
// of the jQuery module (multiple sections open at once, no `exclusive: false`
// re-init dance every time a v-if section appears).
.deliverable-sections {
  &__toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 0.35rem;
  }

  &__toggle-all {
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.8rem;
    cursor: pointer;

    &:hover {
      color: var(--color-primary-hover, #e06b5a);
    }
  }
}

.deliverable-section {
  border-top: 1px solid var(--border-light, #e8e8e8);

  &:last-child {
    border-bottom: 1px solid var(--border-light, #e8e8e8);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    margin: 0;
    padding: 0.75rem 0.15rem;
    border: 0;
    background: none;
    font-family: inherit;
    text-align: left;
    cursor: pointer;

    &:hover .deliverable-section__label {
      color: var(--color-primary-hover, #e06b5a);
    }

    &:focus-visible {
      outline: 2px solid var(--color-primary-hover, #e06b5a);
      outline-offset: -2px;
    }
  }

  &__caret {
    flex: 0 0 auto;
    width: 0.8rem;
    margin: 0;
    color: rgba(0, 0, 0, 0.35);
    transition: transform 0.15s ease;

    .deliverable-section__header.is-open & {
      transform: rotate(90deg);
    }
  }

  &__label {
    flex: 1 1 auto;
    color: rgba(0, 0, 0, 0.87);
    font-size: 0.92rem;
    font-weight: 700;
    transition: color 0.15s ease;
  }

  &__summary {
    flex: 0 0 auto;
    color: rgba(0, 0, 0, 0.4);
    font-size: 0.78rem;
    font-weight: 400;
    white-space: nowrap;
  }

  &__content {
    padding: 0 0.15rem 1rem;

    // The sections supply their own separation now, so the trailing margin
    // Fomantic puts on the last field would just add dead space.
    > .field:last-child,
    > .ui.form .field:last-child {
      margin-bottom: 0;
    }
  }
}

// Sits beside the picker's own inline <small>; without the block the two run
// together as one paragraph.
.outbound-links-hint {
  display: block;
  margin-top: 0.4rem;
}

// Phones / narrow mobile web (wave convention: desktop web byte-identical).
// The card's delete chip was a ~24px hover-ish target; sized up and pulled
// inside the viewport (the default floating-label offset hung it half off
// the card's right edge).
@media only screen and (max-width: 768px) {
  a.ui.top.right.floating.circular.icon.label {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    min-height: 40px;
    left: auto;
    right: 4px;
    transform: none;
  }
}

// Native (deliverables sheet): long URLs clipped raw at the input's edge
// ("…facebook.com/intotheg") -- ellipsize the resting value so truncation
// reads intentional; focusing restores normal caret editing.
html[data-hotwire-native] input[name="url"]:not(:focus) {
  text-overflow: ellipsis;
}

// One Warm Light: the sheet's sticky Save Deliverables owns the solid
// coral; this per-card mid-flow Add takes the same quiet tint as the
// paste-box Add (formMultiple.vue).
html[data-hotwire-native] .ui.buttons.compact > a.ui.button.primary {
  background: rgba(244, 128, 111, 0.15) !important;
  color: var(--color-primary-hover, #e06b5a) !important;
}
</style>
